import { UploadError } from '@/helpers/uploadWithProgress'
import logger from '@/helpers/logger'

/** Attempts in total, including the first. Three is two retries. */
export const MAX_UPLOAD_ATTEMPTS = 3

const BASE_DELAY_MS = 800

/**
 * Wait, with exponential backoff and jitter, unless canceled.
 *
 * The jitter is not decoration. A crew of four uploading from one truck on one
 * cell tower will hit the same dead spot at the same moment, and a fixed
 * backoff marches all four into the same retry instant — turning one outage
 * into a self-inflicted thundering herd. Spreading each wait across a window
 * breaks that up.
 */
async function backoff(attempt: number, signal?: AbortSignal): Promise<void> {
  const ceiling = BASE_DELAY_MS * 2 ** (attempt - 1)
  const delay = ceiling / 2 + Math.random() * (ceiling / 2)

  await new Promise<void>((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(new UploadError('Upload canceled', 'aborted'))
      return
    }
    const timer = setTimeout(resolve, delay)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(new UploadError('Upload canceled', 'aborted'))
      },
      { once: true },
    )
  })
}

/**
 * Run an upload, retrying the kinds of failure that retrying can fix.
 *
 * `attempt` is a fresh async function per try rather than a value, because what
 * has to be repeated differs by storage. A presigned PUT to R2 is idempotent —
 * same key, same bytes, same result — so its retry is the identical request.
 * A Cloudflare Stream direct-upload URL is ONE-SHOT: once a POST has touched
 * it, retrying against the same URL is not something to rely on, so the video
 * path re-reserves and comes back with a new one. Making the caller supply the
 * whole attempt is what lets both live here.
 *
 * A 4xx is never retried. The host looked at the request and refused it, and
 * three more identical refusals only make a person wait longer for the same
 * answer — see `UploadError.isRetriable`.
 */
export async function withUploadRetry(
  attempt: (attemptNumber: number) => Promise<void>,
  options: { signal?: AbortSignal; label: string },
): Promise<void> {
  /*
   * Every path out of this loop either returns or throws — the last attempt
   * rethrows rather than falling through — so there is no "ran out of
   * attempts" case to carry an error forward for.
   */
  for (let n = 1; n <= MAX_UPLOAD_ATTEMPTS; n += 1) {
    try {
      await attempt(n)
      return
    } catch (caught) {
      const retriable = caught instanceof UploadError && caught.isRetriable
      if (!retriable || n === MAX_UPLOAD_ATTEMPTS) {
        throw caught
      }
      logger.warn('upload: retrying', {
        label: options.label,
        attempt: n,
        of: MAX_UPLOAD_ATTEMPTS,
        reason: caught instanceof Error ? caught.message : 'unknown',
      })
      await backoff(n, options.signal)
    }
  }
}
